import { useTests } from '@dyl/systems/TestsManager';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { createTokens, getAllTokens } from '../../../apiClients/backendApi';

import useExitPrompt from '../../../hooks/useExitPrompt';
import useTokensDownload from '../useTokensDownload';
import { useLevels } from "@dyl/systems/LevelsManager";

/**
 * @template T
 * @typedef {[T, React.Dispatch<React.SetStateAction<T>]} UseState
 */

export default function useLogic() {
    const { enqueueSnackbar } = useSnackbar();
    const { globalLevel } = useLevels();
    const location = useLocation();
    const isChina = location.pathname.includes('_cn');

    /** @type {UseState<'' | 'LIVE' | 'PREGRADING' | 'SAMPLE' | 'REVIEW'>} */
    const [type, setType] = useState('');

    /** @type {UseState<string>} */
    const [testId, setTestId] = useState(null);

    const [tokenCount, setTokenCount] = useState(0);
    const [description ,setDescription] = useState('');

    const downloadTokens = useTokensDownload();

    const { tests: allTests, testsStatus } = useTests();
    const isLoadingTests = testsStatus !== 'complete';

    const [isGenerating, setGenerating] = useState(false);
    const [isExporting, setExporting] = useState(false);

    /** @type {UseState<string>} */
    const [outputBatchId, setBatchId] = useState(null);

    const onGeneratePress = async () => {
        setGenerating(true);

        try {
            const res = await createTokens({
                level_id: globalLevel.level_id,
                description,
                number_of_tokens: tokenCount,
                type,
                test_group_id: (
                    // Ensure null for live tests
                    type === 'LIVE' ? null
                    : testId
                ),
                useChinaUrl: isChina
            });

            setBatchId(res.batch_id);

            enqueueSnackbar('Passwords created', { variant: 'success' });
        }
        catch {
            enqueueSnackbar('Failed to create passwords', { variant: 'error' });
        }
        finally {
            setGenerating(false);
        }
    };

    const onExportPress = async () => {
        setExporting(true);

        try {
            const tokens = await getAllTokens(globalLevel.level_id,
                { batch_id: outputBatchId }, isChina
            );

            downloadTokens(tokens);
        }
        catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to download passwords', { variant: 'error' });
        }
        finally {
            setExporting(false);
        }
    };

    const allowGenerate = (
        !!type
        && (!!testId || type === 'LIVE')
        && tokenCount > 0
        && !!description
        && !isGenerating
        && !outputBatchId
    );

    const allowExport = !!outputBatchId && !isExporting;

    const allowEdit = !isGenerating && !outputBatchId;

    useExitPrompt(isGenerating || isExporting);

    return {
        type, setType,
        isLoadingTests, allTests,
        testId, setTestId,
        tokenCount, setTokenCount,
        description, setDescription,
        allowGenerate, onGeneratePress,
        allowExport, onExportPress,
        allowEdit,
        isChina,
    };
}